import { format, formatDistance, isValid, parseISO } from 'date-fns';
import { RSSFeedItem } from '../services/usGovernmentIntegrations';
import { getFeedById, RSS_FEED_REGISTRY, RSSFeedSource } from '../services/rssFeedRegistry';

const GENERIC_RSS_SOURCE_LABELS = new Set([
  'rss_feed',
  'rss_feeds',
  'rss',
  'unknown',
  'unknown source',
]);

export function isGenericRssSource(source: string | undefined | null): boolean {
  if (!source) {
    return true;
  }

  const normalized = source.trim().toLowerCase();
  return GENERIC_RSS_SOURCE_LABELS.has(normalized);
}

export function extractHostname(url: string): string | null {
  try {
    return normalizeHostname(new URL(url).hostname);
  } catch {
    return null;
  }
}

function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, '');
}

function feedUrlHostnames(feed: RSSFeedSource): string[] {
  const hosts = new Set<string>();

  for (const candidate of [feed.url, feed.final_url]) {
    if (!candidate) {
      continue;
    }

    const host = extractHostname(candidate);
    if (host) {
      hosts.add(host);
    }
  }

  return Array.from(hosts);
}

function hostnamesMatch(candidateHost: string, feedHost: string): boolean {
  return (
    candidateHost === feedHost ||
    candidateHost.endsWith(`.${feedHost}`) ||
    feedHost.endsWith(`.${candidateHost}`)
  );
}

function parsePublishedDate(pubDate: string): Date | null {
  if (!pubDate) {
    return null;
  }

  const isoDate = parseISO(pubDate);
  if (isValid(isoDate)) {
    return isoDate;
  }

  const fallback = new Date(pubDate);
  return isValid(fallback) ? fallback : null;
}

export function formatPublishedAt(pubDate: string): string {
  const date = parsePublishedDate(pubDate);
  if (!date) {
    return 'Unknown date';
  }

  return format(date, 'MMM d, yyyy h:mm a');
}

export function formatPublishedAtWithRelative(pubDate: string, now: Date = new Date()): string {
  const date = parsePublishedDate(pubDate);
  if (!date) {
    return 'Unknown date';
  }

  const absolute = format(date, 'MMM d, yyyy h:mm a');
  const relative = formatDistance(date, now, { addSuffix: true });
  return `${relative} · ${absolute}`;
}

export function resolveRssFeedSource(
  item: Pick<RSSFeedItem, 'feedId' | 'source' | 'link'>,
  registry: RSSFeedSource[] = RSS_FEED_REGISTRY
): RSSFeedSource | undefined {
  if (item.feedId) {
    const byId = getFeedById(item.feedId) ?? registry.find((feed) => feed.id === item.feedId);
    if (byId) {
      return byId;
    }
  }

  const linkHost = item.link ? extractHostname(item.link) : null;
  if (linkHost) {
    const byLinkHost = registry.find((feed) =>
      feedUrlHostnames(feed).some((feedHost) => hostnamesMatch(linkHost, feedHost))
    );
    if (byLinkHost) {
      return byLinkHost;
    }
  }

  if (item.source?.startsWith('http')) {
    const sourceHost = extractHostname(item.source);
    if (sourceHost) {
      const bySourceHost = registry.find((feed) =>
        feedUrlHostnames(feed).some((feedHost) => hostnamesMatch(sourceHost, feedHost))
      );
      if (bySourceHost) {
        return bySourceHost;
      }
    }
  }

  if (item.source && !isGenericRssSource(item.source)) {
    const normalizedSource = item.source.toLowerCase();
    const byName = registry.find((feed) => normalizedSource.includes(feed.name.toLowerCase()));
    if (byName) {
      return byName;
    }
  }

  return undefined;
}

/** Publisher homepage for a feed — registry homepageUrl or site origin derived from feed URL. */
export function getFeedHomepageUrl(feed: RSSFeedSource): string | null {
  const homepage = (feed as RSSFeedSource & { homepageUrl?: string }).homepageUrl;
  if (homepage?.trim()) {
    return homepage.trim();
  }

  for (const candidate of [feed.url, feed.final_url]) {
    if (!candidate) {
      continue;
    }
    try {
      const parsed = new URL(candidate);
      return `${parsed.protocol}//${parsed.hostname}/`;
    } catch {
      continue;
    }
  }

  return null;
}

export function getFeedSourceDisplayName(
  item: Pick<RSSFeedItem, 'feedId' | 'source' | 'link'>,
  feed?: RSSFeedSource,
  registry?: RSSFeedSource[]
): string {
  const resolved = feed ?? resolveRssFeedSource(item, registry);
  if (resolved) {
    return resolved.name;
  }

  if (item.source && !isGenericRssSource(item.source)) {
    if (item.source.startsWith('http')) {
      return extractHostname(item.source) ?? item.source;
    }
    return item.source;
  }

  if (item.link) {
    const host = extractHostname(item.link);
    if (host) {
      return host;
    }
  }

  return 'Unknown source';
}
